// src/screens/FinancialsScreen.js
// ============================================================================
// FINANCIAL OVERVIEW - Platform-wide financial metrics and reporting
//
// Computes all metrics directly from Firestore collections:
//   - tournaments + matches -> Revenue, GMV, event economics
//   - wallets -> Cash position, user balances (platform liability)
//   - wallet_ledger -> Deposit/withdrawal flow
//   - withdrawal_requests -> Pending withdrawal queue
//   - users -> User economics, verification rates
//
// LEGAL COMPLIANCE: Per CUSTODIAL_TERMINOLOGY_GUIDE.md
// - CritCash is a CUSTODIAN, not a prize sponsor
// - "Platform Fee Revenue" not "Prize Revenue"
// - "Distributed to Players" not "Paid to Winners"
// - "Custodial Holdings" not "Escrow"
// ============================================================================

import React, { useEffect, useState, useCallback } from 'react';
import {
  View, Text, TouchableOpacity, ActivityIndicator,
  StyleSheet, SafeAreaView, ScrollView, RefreshControl,
} from 'react-native';

import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { EXPENSE_CATEGORIES, STRIPE_ATLAS_FEE_WAIVER_EXPIRES } from '../constants/expenseCategories';

// --- Formatting Helpers ---
const usd = (cents) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((cents || 0) / 100);
const num = (n) => new Intl.NumberFormat('en-US').format(n || 0);
const pct = (v) => (v || 0).toFixed(1) + '%';

// Creator-comp P&L labels (adminGetCompOverview). The YTD net counts
// ledger-paid comp, plus the unpaid remainder only while comp is live (the
// current month's projection); each month says how it was counted in
// m.comp.basis. An older server sends no basis -> today's label, no warnings.
const compNetLabelFor = (months) => {
  const bases = (months || []).map((m) => m?.comp?.basis ?? m?.platform?.compBasis).filter((b) => typeof b === 'string');
  if (!bases.length) return 'Net platform profit (after comp + costs)';
  return bases.some((b) => b === 'projected' || b === 'paid_plus_remaining')
    ? 'Net platform profit YTD (incl. projected remainder)'
    : 'Net platform profit YTD';
};
const compMonthName = (monthKey) => {
  const [y, m] = String(monthKey || '').split('-').map(Number);
  if (!y || !m) return monthKey;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
};
// Reads the server could not do: the YTD net is missing that cost.
const compIncompleteWarnings = (yearTotals) => {
  const out = [];
  if (yearTotals?.expensesError != null && yearTotals.expensesError !== false) out.push('Incomplete: expenses could not be read');
  const failed = Array.isArray(yearTotals?.projectionErrors) ? yearTotals.projectionErrors : [];
  if (failed.length) out.push(`Incomplete: projection failed for ${failed.map(compMonthName).join(', ')}`);
  return out;
};

const COLORS = {
  GOLD: '#FCD000',
  BG: '#0b0b0b',
  CARD: '#101010',
  TEXT: '#EDEDED',
  DIM: '#A0A0A0',
  BORDER: '#1a1a1a',
  SUCCESS: '#2ECC71',
  DANGER: '#E74C3C',
  BLUE: '#3498DB',
  PURPLE: '#9B59B6',
  ORANGE: '#E67E22',
  TEAL: '#1ABC9C',
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function FinancialsScreen({ navigation }) {
  const { user, isAdmin, isLoading } = useAdminAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [backfillingCogs, setBackfillingCogs] = useState(false);
  const [cogsBackfillResult, setCogsBackfillResult] = useState(null);
  const [backfillingDeliveryEstimates, setBackfillingDeliveryEstimates] = useState(false);
  const [deliveryBackfillResult, setDeliveryBackfillResult] = useState(null);
  const [comp, setComp] = useState(null); // creator-comp rollup (ties in the comp engine)

  // Pull the comp engine's own numbers so this screen reconciles with the
  // Comp Control Panel (one source of truth). Kept in its own effect so a
  // comp fetch hiccup never blocks the main financials load.
  useEffect(() => {
    if (isLoading || !isAdmin) return;
    (async () => {
      try {
        const fn = httpsCallable(getFunctions(), 'adminGetCompOverview');
        const res = await fn({ year: new Date().getUTCFullYear() });
        setComp(res.data || null);
      } catch (e) {
        console.warn('[Financials] comp overview load failed:', e.message);
      }
    })();
  }, [isLoading, isAdmin]);

  // ============================================================================
  // ACTION: Backfill Printful delivery estimates for orders missing dates
  // ============================================================================
  // Re-queries Printful /shipping/rates for any merchOrder that has a
  // shippingAddress + syncVariantId but no estimatedDeliveryMin/Max set.
  // Surfaced after a dogfood Coffee Mug order shipped without a date in
  // the order-history card OR the confirmation email; root cause was a
  // silent Printful field-name mismatch (camelCase vs snake_case) since
  // patched in fetchPrintfulDeliveryEstimate. This button catches the
  // orders placed before the fix.
  const runDeliveryEstimateBackfill = useCallback(async () => {
    setBackfillingDeliveryEstimates(true);
    setDeliveryBackfillResult(null);
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'adminBackfillMerchDeliveryEstimates');
      const result = await fn({ limit: 200 });
      setDeliveryBackfillResult({ ok: true, ...result.data });
    } catch (err) {
      setDeliveryBackfillResult({
        ok: false,
        error: err?.message || 'Backfill failed',
      });
    } finally {
      setBackfillingDeliveryEstimates(false);
    }
  }, []);

  // ============================================================================
  // ACTION: Backfill Printful COGS for orders missing cost data
  // ============================================================================
  const runCogsBackfill = useCallback(async () => {
    setBackfillingCogs(true);
    setCogsBackfillResult(null);
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'adminBackfillMerchCogs');
      const result = await fn({ limit: 200 });
      setCogsBackfillResult({
        ok: true,
        ...result.data,
      });
      // Re-pull financials so the screen reflects the newly-captured COGS.
      await loadFinancials();
    } catch (err) {
      setCogsBackfillResult({
        ok: false,
        error: err?.message || 'Backfill failed',
      });
    } finally {
      setBackfillingCogs(false);
    }
  }, []);

  useEffect(() => {
    if (!isLoading && isAdmin) {
      loadFinancials();
    } else if (!isLoading) {
      setLoading(false);
    }
  }, [isAdmin, isLoading]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadFinancials();
    setRefreshing(false);
  }, []);

  // ============================================================================
  // DATA LOADER - Queries Firestore directly for all financial data
  // ============================================================================

  const loadFinancials = async () => {
    try {
      const db = getFirestore();

      // Parallel fetch core collections. Note on what's NOT here vs what
      // older versions of this file fetched:
      //
      //   - No 'wallets' collection: CritCash stores wallet balances on
      //     users/{uid}.balanceCents directly. There is no separate wallets/
      //     collection (verified via 2026-05-25 audit — was returning 0 docs).
      //     Total user balances are summed from usersSnap below.
      //
      //   - 'matches' (root) is still fetched defensively but should be 0
      //     docs in normal operation — per BACKLOG architecture, canonical
      //     event path is tournaments/{tId}/matches/{mId} (subcollection).
      //     If anything appears in root matches/, it's legacy data and the
      //     processEvents() call below will tally it harmlessly.
      const [tourneysSnap, matchesSnap, usersSnap, adCampaignsSnap] = await Promise.all([
        getDocs(collection(db, 'tournaments')),
        getDocs(collection(db, 'matches')),
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'ad_campaigns')),
      ]);

      // Merch orders is read OUT of the core Promise.all so a rules-deny
      // or query failure on this single collection can't blank the whole
      // financials screen. Default to an empty snapshot on failure — the
      // aggregation loop below will produce a zeroed merch summary, and
      // the Merch Store section will render the "no merch sales yet"
      // empty state instead of disappearing.
      //
      // History: 2026-05-30 the first deploy of merch aggregation hit
      // default-deny because `merchOrders` had no Firestore rule. The
      // rule was added, but the resilience pattern stays so a future
      // rules drift fails small (hide the section) instead of failing
      // large (blank screen).
      let merchOrdersSnap = { size: 0, docs: [], forEach: () => {} };
      try {
        merchOrdersSnap = await getDocs(collection(db, 'merchOrders'));
      } catch (e) {
        console.warn('[Financials] Could not read merchOrders (rules / network):', e?.message || e);
      }

      // Optional: advertisers + daily stats
      let adData = {
        totalSpentCents: 0,
        totalBudgetCents: 0,
        totalImpressions: 0,
        totalClicks: 0,
        activeCampaigns: 0,
        totalCampaigns: 0,
        advertisers: 0,
        dailyStats: [],
      };
      try {
        // Campaigns
        adCampaignsSnap.docs.forEach(d => {
          const c = d.data();
          adData.totalCampaigns++;
          adData.totalSpentCents += c.spentCents || 0;
          adData.totalBudgetCents += c.budgetCents || 0;
          adData.totalImpressions += c.totalImpressions || 0;
          adData.totalClicks += c.totalClicks || 0;
          if (c.status === 'active') adData.activeCampaigns++;
        });

        // Advertisers count
        const advertisersSnap = await getDocs(collection(db, 'advertisers'));
        adData.advertisers = advertisersSnap.size;

        // Daily stats for chart (last 14 days)
        const adStatsSnap = await getDocs(collection(db, 'ad_daily_stats'));
        let dailyRaw = adStatsSnap.docs.map(d => d.data());
        dailyRaw.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
        adData.dailyStats = dailyRaw.slice(-14);
      } catch (e) {
        console.log('[Financials] Could not read ad data:', e.message);
      }

      // Withdrawals — read the canonical 'withdrawals' collection (the legacy
      // 'withdrawal_requests' is unused; the 2026-05-24 audit confirmed it has
      // no live writers). Also accumulates turbo-fee revenue here in the same
      // pass since the data is already in hand.
      //
      // Turbo fee revenue model: for each turbo withdrawal in a committed
      // status (processing | completed), feeCents is what we charged the user
      // and stripeInstantFeeCents is what Stripe took for the instant payout.
      // Net retained = collected − absorbed (typically ~0.5% margin on the
      // gross withdrawal amount).
      let withdrawals = {
        pending: 0, pendingCents: 0, completed: 0, completedCents: 0, total: 0,
        turbo: { count: 0, collectedCents: 0, absorbedCents: 0, netRevenueCents: 0 },
      };
      try {
        const wdSnap = await getDocs(collection(db, 'withdrawals'));
        wdSnap.docs.forEach(d => {
          const wd = d.data();
          withdrawals.total++;
          if (['pending', 'pending_hold'].includes(wd.status)) {
            withdrawals.pending++;
            withdrawals.pendingCents += wd.amountCents || 0;
          } else if (wd.status === 'completed') {
            withdrawals.completed++;
            withdrawals.completedCents += wd.amountCents || 0;
          }

          if (wd.method === 'turbo' && ['processing', 'completed'].includes(wd.status)) {
            withdrawals.turbo.count++;
            withdrawals.turbo.collectedCents += wd.feeCents || 0;
            withdrawals.turbo.absorbedCents += wd.stripeInstantFeeCents || 0;
          }
        });
        withdrawals.turbo.netRevenueCents =
          withdrawals.turbo.collectedCents - withdrawals.turbo.absorbedCents;
      } catch (e) {
        console.log('[Financials] Could not read withdrawals:', e.message);
      }

      // Categorize wallet_ledger entries by REASON (not type — type is just
      // 'credit'/'debit' direction; reason carries the semantic purpose).
      // Withdrawal totals are net of refunds: a 'withdrawal_request' debit
      // followed by a 'withdrawal_failed' / 'withdrawal_canceled' /
      // 'aml_review_blocked' credit means the user's wallet was made whole,
      // so the gross debit shouldn't count toward "money that left." Net it
      // so the "Total Withdrawals" KPI reflects funds that ACTUALLY left
      // the platform, not gross attempted withdrawals.
      let ledger = { deposits: 0, withdrawals: 0, entryFees: 0, distributions: 0, giftCards: 0, txCount: 0 };
      try {
        const ledgerSnap = await getDocs(collection(db, 'wallet_ledger'));
        let withdrawalDebits = 0;
        let withdrawalRefunds = 0;
        ledgerSnap.docs.forEach(d => {
          const entry = d.data();
          const amt = Math.abs(entry.amountCents || 0);
          const reason = entry.reason;
          ledger.txCount++;

          if (reason === 'stripe_deposit') {
            ledger.deposits += amt;
          } else if (reason === 'withdrawal_request') {
            withdrawalDebits += amt;
          } else if (reason === 'withdrawal_failed' || reason === 'withdrawal_canceled' || reason === 'aml_review_blocked') {
            withdrawalRefunds += amt;
          } else if (reason === 'entry_fee' || reason === 'event_entry') {
            ledger.entryFees += amt;
          } else if (reason === 'pool_distribution' || reason === 'event_payout' || reason === 'winner_payout') {
            ledger.distributions += amt;
          } else if (reason === 'gift_card_redeem' || reason === 'gift_card_purchase') {
            ledger.giftCards += amt;
          }
        });
        ledger.withdrawals = Math.max(0, withdrawalDebits - withdrawalRefunds);
      } catch (e) {
        console.log('[Financials] Could not read wallet_ledger:', e.message);
      }

      // ================================================================
      // COMPUTE: Event Economics
      // ================================================================

      let gmvAllTime = 0;
      let revenueRealized = 0;      // Platform fees from completed/finalized
      let revenueInPlay = 0;        // Platform fees from live events
      let revenueProjected = 0;     // Platform fees from created/locked events
      let distributedTotal = 0;     // Pools already distributed
      let poolsInPlay = 0;          // Pools in live events
      let poolsProjected = 0;       // Pools in created events
      let totalEvents = 0;
      let totalEntrants = 0;

      const statusCounts = {};
      const gameBreakdown = {};
      const eventTypeBreakdown = { tournament: 0, match: 0 };

      const processEvents = (snap, type) => {
        snap.docs.forEach(d => {
          const ev = d.data();
          const entryFee = ev.entryFeeCents || 0;
          const entrants = ev.entrantsCount || 0;
          const gross = entryFee * entrants;
          const platformFee = ev.platformFeeCents || Math.floor(gross * 0.10);
          const pool = ev.prizePoolCents || (gross - platformFee);
          const status = ev.status || 'unknown';
          const game = ev.game || 'Unknown';

          totalEvents++;
          totalEntrants += entrants;
          gmvAllTime += gross;
          eventTypeBreakdown[type]++;

          // Status counts
          statusCounts[status] = (statusCounts[status] || 0) + 1;

          // Per-game breakdown
          if (!gameBreakdown[game]) {
            gameBreakdown[game] = { events: 0, gmvCents: 0, feeCents: 0, entrants: 0 };
          }
          gameBreakdown[game].events++;
          gameBreakdown[game].gmvCents += gross;
          gameBreakdown[game].feeCents += platformFee;
          gameBreakdown[game].entrants += entrants;

          // Categorize by lifecycle stage
          if (['completed', 'finalized'].includes(status)) {
            revenueRealized += platformFee;
            distributedTotal += pool;
          } else if (status === 'live') {
            revenueInPlay += platformFee;
            poolsInPlay += pool;
          } else if (['created', 'locked', 'seeded'].includes(status)) {
            revenueProjected += platformFee;
            poolsProjected += pool;
          }
        });
      };

      processEvents(tourneysSnap, 'tournament');
      processEvents(matchesSnap, 'match');

      // ================================================================
      // COMPUTE: Merch Store
      // ================================================================
      // Status mapping mirrors reconciliation.js MERCH_PAID_STATUSES —
      // any post-payment status counts as money received from Stripe.
      // Pre-payment statuses (pending, failed_checkout_creation) are
      // excluded — no charge was completed. printfulCostCents may be
      // missing on older orders that predate COGS capture; show net
      // margin as null in that case rather than treating COGS as 0.
      const MERCH_PAID_STATUSES = new Set([
        'paid',
        'submitted_to_printful',
        'payment_received_fulfillment_failed',
        'shipped',
        'printful_failed',
        'printful_canceled',
        'returned',
        'printful_status_changed',
      ]);
      const STRIPE_FEE_PCT = 0.029;
      const STRIPE_FEE_FIXED_CENTS = 30;

      const merch = {
        totalOrders: merchOrdersSnap.size,
        paidOrders: 0,
        pendingOrders: 0,
        shippedOrders: 0,
        problemOrders: 0,
        grossRevenueCents: 0,
        estimatedStripeFeesCents: 0,
        printfulCogsCents: 0,
        ordersMissingCogs: 0,
        ordersWithCogs: 0,
        productBreakdown: {},
      };

      merchOrdersSnap.docs.forEach((d) => {
        const o = d.data() || {};
        const status = o.status;
        // Use the order's totalCents (set on every order — includes
        // quantity × unitAmountCents for both legacy single-item and
        // new multi-item cart orders). The previous unitAmountCents
        // read was undercounting any quantity>1 order — e.g. a 2-mug
        // order at $15/unit registered as $15 revenue instead of $30,
        // and a multi-item cart was double-undercount-bad. Fall back
        // to unitAmountCents × quantity if totalCents somehow missing.
        const cogs = o.printfulCostCents || 0;
        const orderTotalCents =
          o.totalCents ||
          ((o.unitAmountCents || 0) * (o.quantity || 1)) ||
          0;

        if (status === 'pending' || status === 'failed_checkout_creation') {
          merch.pendingOrders++;
          return;
        }
        if (!MERCH_PAID_STATUSES.has(status)) {
          merch.pendingOrders++;
          return;
        }

        merch.paidOrders++;
        merch.grossRevenueCents += orderTotalCents;
        if (orderTotalCents > 0) {
          merch.estimatedStripeFeesCents +=
            Math.round(orderTotalCents * STRIPE_FEE_PCT) + STRIPE_FEE_FIXED_CENTS;
        }
        if (status === 'shipped') merch.shippedOrders++;
        if (['printful_failed', 'printful_canceled', 'returned', 'payment_received_fulfillment_failed'].includes(status)) {
          merch.problemOrders++;
        }

        if (cogs > 0) {
          merch.printfulCogsCents += cogs;
          merch.ordersWithCogs++;
        } else {
          merch.ordersMissingCogs++;
        }

        // Per-product breakdown — iterate lineItems[] when present so
        // multi-item carts attribute revenue to every product they
        // contain (not just the head item). Falls back to the scalar
        // productKey/quantity for legacy single-item orders that
        // predate the lineItems[] field.
        //
        // COGS allocation: Printful's per-line cost breakdown isn't
        // stored separately on the order doc (only the total). For
        // multi-item orders we apportion COGS by each line's share of
        // the order's gross revenue — imperfect when item margins
        // differ wildly, but accurate for COGS-vs-revenue at the
        // product level when items have similar markup. Single-item
        // orders attribute 100% of COGS to the one line as before.
        const lines = Array.isArray(o.lineItems) && o.lineItems.length > 0
          ? o.lineItems
          : [{
              productKey: o.productKey || 'unknown',
              productName: o.productName || o.productKey || 'unknown',
              quantity: o.quantity || 1,
              unitAmountCents: o.unitAmountCents || 0,
              lineTotalCents: orderTotalCents,
            }];

        lines.forEach((line) => {
          const productKey = line.productKey || 'unknown';
          const lineTotal = line.lineTotalCents ||
            ((line.unitAmountCents || 0) * (line.quantity || 1)) ||
            0;
          const lineQty = line.quantity || 1;
          // Allocate COGS proportional to this line's share of the
          // order total (defaults to full share for single-line orders).
          const lineCogsShare = (cogs > 0 && orderTotalCents > 0)
            ? Math.round(cogs * (lineTotal / orderTotalCents))
            : 0;

          if (!merch.productBreakdown[productKey]) {
            merch.productBreakdown[productKey] = {
              productKey,
              productName: line.productName || productKey,
              units: 0,    // shown as "(N)" next to product name — unit count
              orders: 0,   // unique paid orders that include this product
                           // — used for the per-product Stripe fixed-fee
                           // estimate (30¢/charge). Multi-item carts share
                           // one Stripe charge so we slightly over-estimate
                           // fees by attributing the fixed component to
                           // every product in the cart; acceptable for a
                           // back-of-envelope per-product margin view.
              grossRevenueCents: 0,
              cogsCents: 0,
              ordersWithCogs: 0,
            };
          }
          const pb = merch.productBreakdown[productKey];
          pb.units += lineQty;
          pb.orders++;
          pb.grossRevenueCents += lineTotal;
          if (lineCogsShare > 0) {
            pb.cogsCents += lineCogsShare;
            pb.ordersWithCogs++;
          }
          if (line.productName && !pb.productName.includes(' ')) {
            pb.productName = line.productName;
          }
        });
      });

      // Net margin: only meaningful when ALL paid orders have COGS data.
      // If any are missing, we expose a "partial" view so the operator
      // can see what's known without misreading "—" as zero margin.
      merch.netMarginCents =
        merch.ordersMissingCogs === 0
          ? merch.grossRevenueCents - merch.estimatedStripeFeesCents - merch.printfulCogsCents
          : null;
      merch.partialNetMarginCents =
        merch.ordersWithCogs > 0
          ? merch.grossRevenueCents - merch.estimatedStripeFeesCents - merch.printfulCogsCents
          : null;
      merch.productList = Object.values(merch.productBreakdown).sort(
        (a, b) => b.grossRevenueCents - a.grossRevenueCents
      );

      // ================================================================
      // COMPUTE: Cash Position (Balance Sheet) + User Economics
      //
      // Single pass over usersSnap covers both — wallet balances live on
      // user docs (users/{uid}.balanceCents), not a separate collection.
      // CritCash has no "held" or "custodial-hold" bucket separate from the
      // main wallet — entry fees debit balanceCents immediately at join time
      // (verified in events.js entry flow), so the "available" vs "held"
      // split that older versions of this file modeled doesn't apply here.
      // totalUserBalances IS the full platform liability.
      //
      // Gift balance (users/{uid}.giftBalanceCents) is a separate column
      // some users have for promotional / refund-reserve credits — summed
      // alongside for the "Total Wallet Value" view.
      //
      // ID verification lives at verification.governmentId.status (set by
      // Stripe Identity webhook handler) — older versions of this file
      // looked at the never-populated u.idVerified flag.
      // ================================================================

      let totalUserBalances = 0;    // Sum of users.{uid}.balanceCents — platform liability
      let totalGiftBalances = 0;    // Sum of giftBalanceCents — promotional credits
      let totalWalletValue = 0;     // = totalUserBalances + totalGiftBalances

      let totalUsers = usersSnap.size;
      let verifiedUsers = 0;        // Phone-verified
      let idVerifiedUsers = 0;      // KYC (Stripe Identity)

      usersSnap.docs.forEach(d => {
        const u = d.data();
        const bal = u.balanceCents || 0;
        const gift = u.giftBalanceCents || 0;
        totalUserBalances += bal;
        totalGiftBalances += gift;
        totalWalletValue += bal + gift;
        if (u.phoneVerified) verifiedUsers++;
        if (u.verification?.governmentId?.status === 'verified') idVerifiedUsers++;
      });

      // CritCash has no separate held-funds bucket; kept as 0 so the Cash
      // Position section's "+ Held (Custodial)" row renders cleanly and the
      // legacy data shape stays backwards-compatible if other consumers
      // reference it.
      const totalHeld = 0;

      const avgBalanceCents = totalUsers > 0 ? Math.round(totalUserBalances / totalUsers) : 0;
      const avgEntryFee = totalEvents > 0 ? Math.round(gmvAllTime / totalEntrants) : 0;
      const avgRevenuePerEvent = totalEvents > 0 ? Math.round((revenueRealized + revenueInPlay) / totalEvents) : 0;
      const fillRate = totalEvents > 0
        ? ((tourneysSnap.docs.reduce((s, d) => s + (d.data().entrantsCount || 0), 0) +
            matchesSnap.docs.reduce((s, d) => s + (d.data().entrantsCount || 0), 0)) /
           (tourneysSnap.docs.reduce((s, d) => s + (d.data().maxEntrants || d.data().entrantsCount || 1), 0) +
            matchesSnap.docs.reduce((s, d) => s + (d.data().maxEntrants || d.data().entrantsCount || 1), 0)) * 100)
        : 0;

      // Sort game breakdown by GMV descending
      const gameRanking = Object.entries(gameBreakdown)
        .map(([game, stats]) => ({ game, ...stats }))
        .sort((a, b) => b.gmvCents - a.gmvCents);

      setData({
        // Revenue
        gmvAllTime,
        revenueRealized,
        revenueInPlay,
        revenueProjected,
        distributedTotal,
        poolsInPlay,
        poolsProjected,

        // Cash Position
        totalUserBalances,
        totalGiftBalances,
        totalHeld,
        totalWalletValue,

        // Withdrawals
        withdrawals,

        // Ledger
        ledger,

        // Events
        totalEvents,
        totalEntrants,
        statusCounts,
        eventTypeBreakdown,
        avgEntryFee,
        avgRevenuePerEvent,
        fillRate,

        // Users
        totalUsers,
        verifiedUsers,
        idVerifiedUsers,
        avgBalanceCents,

        // Breakdowns
        gameRanking,

        // Advertiser Revenue
        adData,

        // Merch Store (Stripe-paid orders + Printful COGS)
        merch,

        // Operating Expenses (populated below from cloud function call)
        expenses: null,
      });

      // Fetch operating-expense aggregations from cloud function.
      // Wrapped in try/catch so an aggregation failure doesn't blank out the
      // rest of the screen — the Operating Expenses section just shows "—".
      try {
        const functions = getFunctions();
        const fn = httpsCallable(functions, 'adminGetExpenseAggregations');
        const result = await fn({});
        setData(prev => prev ? { ...prev, expenses: result.data } : prev);
      } catch (e) {
        console.warn('[Financials] expense aggregations failed:', e?.message || e);
      }

    } catch (error) {
      console.error('[Financials] Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // RENDER: Loading / Access Denied
  // ============================================================================

  if (isLoading || !user) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.centerWrap}>
          <ActivityIndicator color={COLORS.GOLD} />
        </View>
      </SafeAreaView>
    );
  }

  if (!isAdmin) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.centerWrap}>
          <Text style={styles.deniedText}>Access Denied</Text>
          <TouchableOpacity onPress={() => navigation.navigate('Lobby')}>
            <Text style={styles.backLink}>Go to Lobby</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  // Direct-lane Super Crits (YTD): net credited straight to creators' earnings
  // when the charge succeeds — never paid by the monthly run, so not in "Comp
  // paid to creators". adminGetCompOverview returns yearTotals.directTipCents
  // (platform block first, then the totals object); an older server omits it
  // -> null -> no row.
  const compDirectTipCents = [comp?.yearTotals?.platform?.directTipCents, comp?.yearTotals?.directTipCents]
    .find((v) => typeof v === 'number' && Number.isFinite(v)) ?? null;
  // Rumbles credited to recipients (YTD, evergreen 3.3): net credited straight
  // to the streamers, squad members and event players who received them —
  // same posture as the Super Crit line. Year block first, else the sum of
  // the months' revenue.rumbleCreditedCents; an older server omits it -> null
  // -> no row.
  const isCentsNum = (v) => typeof v === 'number' && Number.isFinite(v);
  const compRumbleCreditedCents = (() => {
    const yearV = [comp?.yearTotals?.platform?.rumbleCreditedCents, comp?.yearTotals?.rumbleCreditedCents].find(isCentsNum);
    if (yearV != null) return yearV;
    const per = (comp?.months || [])
      .map((m) => [m?.platform?.rumbleCreditedCents, m?.revenue?.rumbleCreditedCents].find(isCentsNum))
      .filter((v) => v != null);
    return per.length ? per.reduce((s, v) => s + v, 0) : null;
  })();
  // Membership credits (YTD, 3.5a): net credited straight to creators from
  // channel membership payments; never in the monthly run. Same fallbacks as
  // the Rumble line; an older server omits it -> null -> no row.
  const compMembershipCreditedCents = (() => {
    const yearV = [comp?.yearTotals?.platform?.membershipCreditedCents, comp?.yearTotals?.membershipCreditedCents].find(isCentsNum);
    if (yearV != null) return yearV;
    const per = (comp?.months || [])
      .map((m) => [m?.platform?.membershipCreditedCents, m?.revenue?.membershipCreditedCents].find(isCentsNum))
      .filter((v) => v != null);
    return per.length ? per.reduce((s, v) => s + v, 0) : null;
  })();

  // ============================================================================
  // RENDER: Main Dashboard
  // ============================================================================

  return (
    <SafeAreaView style={styles.safeArea}>
      <Header navigation={navigation} />
      <ScrollView
        contentContainerStyle={styles.container}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={COLORS.GOLD} />}
      >
        <View style={styles.header}>
          <Text style={styles.title}>Financial Overview</Text>
          <Text style={styles.subtitle}>Platform-wide accounting and metrics</Text>
        </View>

        <TouchableOpacity style={styles.backButton} onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}>
          <Ionicons name="arrow-back" size={14} color={COLORS.GOLD} />
          <Text style={styles.backButtonText}> Back to Admin Hub</Text>
        </TouchableOpacity>

        {/* Creator Compensation — pulled from the comp engine so this screen
            reconciles with the Comp Control Panel (its numbers are authoritative). */}
        {comp && (
          <View style={styles.compCard}>
            <View style={styles.compHead}>
              <Text style={styles.compTitle}>Creator Compensation</Text>
              <View style={[styles.compBadge, comp.compTestMode ? styles.compBadgeTest : styles.compBadgeLive]}>
                <Text style={styles.compBadgeTxt}>{comp.compEnabled ? (comp.compTestMode ? 'TEST' : 'LIVE') : 'OFF'}</Text>
              </View>
            </View>
            <View style={styles.compRow}><Text style={styles.compLabel}>Comp-funding revenue (YTD)</Text><Text style={styles.compVal}>{usd(comp.yearTotals?.compFundingCents)}</Text></View>
            <View style={styles.compRow}><Text style={styles.compLabel}>Comp paid to creators</Text><Text style={[styles.compVal, { color: COLORS.SUCCESS }]}>{usd(comp.yearTotals?.paidCents)}</Text></View>
            {compDirectTipCents != null && (
              <View style={styles.compRow}><Text style={styles.compLabel}>Super Crits credited to creators</Text><Text style={[styles.compVal, { color: COLORS.SUCCESS }]}>{usd(compDirectTipCents)}</Text></View>
            )}
            {compRumbleCreditedCents != null && (
              <View style={styles.compRow}><Text style={styles.compLabel}>Rumbles credited to recipients</Text><Text style={[styles.compVal, { color: COLORS.SUCCESS }]}>{usd(compRumbleCreditedCents)}</Text></View>
            )}
            {compMembershipCreditedCents != null && (
              <View style={styles.compRow}><Text style={styles.compLabel}>Membership credits</Text><Text style={[styles.compVal, { color: COLORS.SUCCESS }]}>{usd(compMembershipCreditedCents)}</Text></View>
            )}
            <View style={styles.compRow}><Text style={styles.compLabel}>Planned (previews + current projection)</Text><Text style={[styles.compVal, { color: COLORS.GOLD }]}>{usd(comp.yearTotals?.plannedCents)}</Text></View>
            <View style={[styles.compRow, styles.compNetRow]}>
              <Text style={styles.compNetLabel}>{compNetLabelFor(comp.months)}</Text>
              <Text style={[styles.compNet, { color: (comp.yearTotals?.platform?.netProfitCents || 0) >= 0 ? COLORS.SUCCESS : COLORS.DANGER }]}>
                {usd(comp.yearTotals?.platform?.netProfitCents)}
              </Text>
            </View>
            {compIncompleteWarnings(comp.yearTotals).map((w) => (
              <Text key={w} style={[styles.compNote, { color: COLORS.DANGER }]}>{w}</Text>
            ))}
            <Text style={styles.compNote}>Excludes merch COGS (shown below). Edit the engine + model scenarios in the control panel.</Text>
            <TouchableOpacity style={styles.compBtn} onPress={() => navigation.navigate('AdminCompOverview')}>
              <Text style={styles.compBtnTxt}>Open Comp Control Panel</Text>
              <Ionicons name="arrow-forward" size={14} color="#000" style={{ marginLeft: 6 }} />
            </TouchableOpacity>
          </View>
        )}

        {loading ? (
          <View style={styles.loadingWrap}>
            <ActivityIndicator color={COLORS.GOLD} size="large" />
            <Text style={[styles.dimText, { marginTop: 12 }]}>Loading financial data...</Text>
          </View>
        ) : !data ? (
          <View style={styles.loadingWrap}>
            <Ionicons name="alert-circle-outline" size={40} color={COLORS.DANGER} />
            <Text style={[styles.dimText, { marginTop: 12 }]}>Failed to load financial data</Text>
            <TouchableOpacity style={styles.retryBtn} onPress={loadFinancials}>
              <Text style={styles.retryBtnText}>Retry</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <>
            {/* ============================================================ */}
            {/* SECTION 1: REVENUE & GMV */}
            {/* ============================================================ */}
            <Section title="Revenue & GMV" icon="cash-outline">
              <View style={styles.kpiRow}>
                <KpiCard
                  label="Gross Merchandise Value"
                  sublabel="All entry fees collected"
                  value={usd(data.gmvAllTime)}
                  color={COLORS.GOLD}
                  iconName="trending-up"
                />
                <KpiCard
                  label="Platform Fee Revenue"
                  sublabel="Realized (completed events)"
                  value={usd(data.revenueRealized)}
                  color={COLORS.SUCCESS}
                  iconName="checkmark-circle"
                />
              </View>
              <View style={styles.kpiRow}>
                <KpiCard
                  label="Revenue In-Play"
                  sublabel="From live events"
                  value={usd(data.revenueInPlay)}
                  color={COLORS.BLUE}
                  iconName="play-circle"
                />
                <KpiCard
                  label="Revenue Pipeline"
                  sublabel="From open/upcoming events"
                  value={usd(data.revenueProjected)}
                  color={COLORS.DIM}
                  iconName="time-outline"
                />
              </View>
              <View style={styles.kpiRow}>
                <KpiCard
                  label="Distributed to Players"
                  sublabel="Pool distributions (completed)"
                  value={usd(data.distributedTotal)}
                  color={COLORS.TEAL}
                  iconName="wallet-outline"
                />
                <KpiCard
                  label="Combined Revenue"
                  sublabel="Platform fees + ad + turbo"
                  value={usd((data.revenueRealized || 0) + (data.adData.totalSpentCents || 0) + (data.withdrawals?.turbo?.netRevenueCents || 0))}
                  color={COLORS.PURPLE}
                  iconName="analytics-outline"
                />
              </View>
              {/* Turbo fee revenue — separate row so it's clearly distinguished
                  from event-based platform fees. Net = what users paid CritCash
                  for the convenience of instant withdrawals minus what Stripe
                  charged CritCash for the instant payouts. */}
              <View style={styles.kpiRow}>
                <KpiCard
                  label="Turbo Fees (collected)"
                  sublabel={`${data.withdrawals?.turbo?.count || 0} turbo withdrawals`}
                  value={usd(data.withdrawals?.turbo?.collectedCents || 0)}
                  color={COLORS.GOLD}
                  iconName="flash-outline"
                />
                <KpiCard
                  label="Turbo Fees (net retained)"
                  sublabel={`Minus ${usd(data.withdrawals?.turbo?.absorbedCents || 0)} Stripe instant fee`}
                  value={usd(data.withdrawals?.turbo?.netRevenueCents || 0)}
                  color={COLORS.SUCCESS}
                  iconName="trending-up-outline"
                />
              </View>
            </Section>

            {/* ============================================================ */}
            {/* SECTION 1B: MERCH STORE (PRINTFUL) */}
            {/* ============================================================ */}
            <Section title="Merch Store" icon="shirt-outline">
              <View style={styles.kpiRow}>
                <KpiCard
                  label="Orders Paid"
                  sublabel={`${data.merch.shippedOrders} shipped${data.merch.problemOrders > 0 ? ` · ${data.merch.problemOrders} problem` : ''}`}
                  value={num(data.merch.paidOrders)}
                  color={COLORS.BLUE}
                  iconName="bag-check-outline"
                />
                <KpiCard
                  label="Gross Merch Revenue"
                  sublabel="Retail charged via Stripe"
                  value={usd(data.merch.grossRevenueCents)}
                  color={COLORS.GOLD}
                  iconName="cash-outline"
                />
              </View>
              <View style={styles.kpiRow}>
                <KpiCard
                  label="Printful COGS"
                  sublabel={
                    data.merch.ordersMissingCogs > 0
                      ? `${data.merch.ordersWithCogs}/${data.merch.paidOrders} captured · ${data.merch.ordersMissingCogs} pending backfill`
                      : `All ${data.merch.ordersWithCogs} captured`
                  }
                  value={usd(data.merch.printfulCogsCents)}
                  color={COLORS.DANGER}
                  iconName="arrow-down-circle-outline"
                />
                <KpiCard
                  label="Net Merch Margin"
                  sublabel={
                    data.merch.netMarginCents !== null
                      ? 'Revenue − Stripe fees − COGS'
                      : data.merch.partialNetMarginCents !== null
                        ? `Partial (${data.merch.ordersWithCogs}/${data.merch.paidOrders} orders)`
                        : 'Awaiting COGS capture'
                  }
                  value={
                    data.merch.netMarginCents !== null
                      ? usd(data.merch.netMarginCents)
                      : data.merch.partialNetMarginCents !== null
                        ? usd(data.merch.partialNetMarginCents) + '*'
                        : '—'
                  }
                  color={COLORS.SUCCESS}
                  iconName="analytics-outline"
                />
              </View>

              {/* Net position breakdown — matches the Cash Position summary
                  style so the P&L reads top to bottom: revenue in → costs
                  out → what's left over. */}
              {data.merch.paidOrders > 0 && (
                <View style={styles.summaryBar}>
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>Gross Revenue ({data.merch.paidOrders} orders)</Text>
                    <Text style={styles.summaryValue}>{usd(data.merch.grossRevenueCents)}</Text>
                  </View>
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>− Stripe Processing (est. 2.9% + 30¢)</Text>
                    <Text style={styles.summaryValueDim}>{usd(data.merch.estimatedStripeFeesCents)}</Text>
                  </View>
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>
                      − Printful COGS{data.merch.ordersMissingCogs > 0 ? ` (${data.merch.ordersWithCogs}/${data.merch.paidOrders} captured)` : ''}
                    </Text>
                    <Text style={styles.summaryValueDim}>{usd(data.merch.printfulCogsCents)}</Text>
                  </View>
                  <View style={styles.divider} />
                  <View style={styles.summaryRow}>
                    <Text style={[styles.summaryLabel, { fontWeight: '700', color: COLORS.TEXT }]}>
                      {data.merch.ordersMissingCogs === 0 ? 'Net Merch Margin' : 'Net Margin (partial)'}
                    </Text>
                    <Text style={[styles.summaryValue, { color: COLORS.SUCCESS }]}>
                      {usd(
                        data.merch.netMarginCents !== null
                          ? data.merch.netMarginCents
                          : (data.merch.partialNetMarginCents || 0)
                      )}
                    </Text>
                  </View>
                  {data.merch.ordersMissingCogs > 0 && (
                    <>
                      <Text style={[styles.summaryLabel, { fontSize: 11, marginTop: 6, fontStyle: 'italic' }]}>
                        * COGS missing on {data.merch.ordersMissingCogs} order{data.merch.ordersMissingCogs === 1 ? '' : 's'}.
                        Pull cost data from Printful to complete the margin.
                      </Text>
                      <TouchableOpacity
                        onPress={runCogsBackfill}
                        disabled={backfillingCogs}
                        style={{
                          marginTop: 10,
                          backgroundColor: backfillingCogs ? COLORS.BORDER : COLORS.GOLD,
                          paddingVertical: 10,
                          paddingHorizontal: 16,
                          borderRadius: 8,
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8,
                        }}
                      >
                        {backfillingCogs ? (
                          <ActivityIndicator size="small" color={COLORS.BG} />
                        ) : (
                          <Ionicons name="cloud-download-outline" size={16} color={COLORS.BG} />
                        )}
                        <Text style={{ color: COLORS.BG, fontSize: 13, fontWeight: '700' }}>
                          {backfillingCogs ? 'Pulling COGS from Printful…' : `Backfill COGS for ${data.merch.ordersMissingCogs} order${data.merch.ordersMissingCogs === 1 ? '' : 's'}`}
                        </Text>
                      </TouchableOpacity>
                    </>
                  )}
                  {cogsBackfillResult && (
                    <Text
                      style={[
                        styles.summaryLabel,
                        {
                          fontSize: 11,
                          marginTop: 8,
                          fontStyle: 'italic',
                          color: cogsBackfillResult.ok ? COLORS.SUCCESS : COLORS.DANGER,
                        },
                      ]}
                    >
                      {cogsBackfillResult.ok
                        ? `Backfilled ${cogsBackfillResult.succeeded || 0} order${cogsBackfillResult.succeeded === 1 ? '' : 's'}${cogsBackfillResult.failed ? ` · ${cogsBackfillResult.failed} failed (check function logs)` : ''}.`
                        : `Backfill failed: ${cogsBackfillResult.error}`}
                    </Text>
                  )}

                  {/* Delivery-estimate backfill — always visible since we
                      don't track a missing-count denominator. Click runs
                      adminBackfillMerchDeliveryEstimates which targets any
                      submitted order with no estimatedDeliveryMin/Max set. */}
                  <TouchableOpacity
                    onPress={runDeliveryEstimateBackfill}
                    disabled={backfillingDeliveryEstimates}
                    style={{
                      marginTop: 10,
                      backgroundColor: backfillingDeliveryEstimates ? COLORS.BORDER : COLORS.BG,
                      borderColor: COLORS.GOLD,
                      borderWidth: 1,
                      paddingVertical: 10,
                      paddingHorizontal: 16,
                      borderRadius: 8,
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                    }}
                  >
                    {backfillingDeliveryEstimates ? (
                      <ActivityIndicator size="small" color={COLORS.GOLD} />
                    ) : (
                      <Ionicons name="calendar-outline" size={16} color={COLORS.GOLD} />
                    )}
                    <Text style={{ color: COLORS.GOLD, fontSize: 13, fontWeight: '700' }}>
                      {backfillingDeliveryEstimates
                        ? 'Querying Printful for delivery dates…'
                        : 'Backfill missing delivery estimates'}
                    </Text>
                  </TouchableOpacity>
                  {deliveryBackfillResult && (
                    <Text
                      style={[
                        styles.summaryLabel,
                        {
                          fontSize: 11,
                          marginTop: 6,
                          fontStyle: 'italic',
                          color: deliveryBackfillResult.ok ? COLORS.SUCCESS : COLORS.DANGER,
                        },
                      ]}
                    >
                      {deliveryBackfillResult.ok
                        ? (deliveryBackfillResult.totalEligible === 0
                          ? 'No orders need delivery estimates — everything is up to date.'
                          : `Backfilled ${deliveryBackfillResult.succeeded || 0} of ${deliveryBackfillResult.totalEligible || 0} order${deliveryBackfillResult.totalEligible === 1 ? '' : 's'}${deliveryBackfillResult.failed ? ` · ${deliveryBackfillResult.failed} failed (Printful returned no date)` : ''}.`)
                        : `Backfill failed: ${deliveryBackfillResult.error}`}
                    </Text>
                  )}
                </View>
              )}

              {/* Per-product breakdown — same shape as Cash Position
                  summary bar so the eye can compare products at a glance.
                  Sorted by gross revenue desc; products with no orders
                  hidden. */}
              {data.merch.productList.length > 0 && (
                <View style={styles.summaryBar}>
                  <Text style={[styles.summaryLabel, { fontWeight: '700', color: COLORS.TEXT, marginBottom: 8 }]}>
                    By Product
                  </Text>
                  {data.merch.productList.map((p) => {
                    // Margin estimate uses orders for the Stripe fixed-fee
                    // portion (30¢ per Stripe charge, not per unit).
                    // (N) shown next to the product name is units sold
                    // since that's what reads as "how many of this product
                    // did we move" — distinct from "how many checkout
                    // sessions touched it" which is orders.
                    const margin = p.ordersWithCogs === p.orders && p.orders > 0
                      ? p.grossRevenueCents - p.cogsCents - (Math.round(p.grossRevenueCents * 0.029) + p.orders * 30)
                      : null;
                    return (
                      <View key={p.productKey} style={styles.summaryRow}>
                        <Text style={[styles.summaryLabel, { flex: 1 }]} numberOfLines={1}>
                          {p.productName} <Text style={{ color: COLORS.DIM, fontSize: 11 }}>({p.units})</Text>
                        </Text>
                        <Text style={[styles.summaryLabel, { width: 90, textAlign: 'right' }]}>
                          {usd(p.grossRevenueCents)}
                        </Text>
                        <Text style={[styles.summaryLabel, { width: 80, textAlign: 'right', color: margin !== null ? COLORS.SUCCESS : COLORS.DIM, fontSize: 12 }]}>
                          {margin !== null ? usd(margin) : '—'}
                        </Text>
                      </View>
                    );
                  })}
                  <View style={[styles.summaryRow, { marginTop: 4 }]}>
                    <Text style={{ flex: 1 }} />
                    <Text style={[styles.summaryLabel, { width: 90, textAlign: 'right', fontSize: 10, color: COLORS.DIM, textTransform: 'uppercase' }]}>
                      Revenue
                    </Text>
                    <Text style={[styles.summaryLabel, { width: 80, textAlign: 'right', fontSize: 10, color: COLORS.DIM, textTransform: 'uppercase' }]}>
                      Net Margin
                    </Text>
                  </View>
                </View>
              )}

              {data.merch.paidOrders === 0 && (
                <View style={styles.summaryBar}>
                  <Text style={[styles.summaryLabel, { textAlign: 'center', paddingVertical: 8 }]}>
                    No merch sales yet. First sale will populate revenue, COGS, and margin here.
                  </Text>
                </View>
              )}
            </Section>

            {/* ============================================================ */}
            {/* SECTION 2: CASH POSITION (BALANCE SHEET) */}
            {/* ============================================================ */}
            <Section title="Cash Position" icon="business-outline">
              <View style={styles.kpiRow}>
                <KpiCard
                  label="Total User Balances"
                  sublabel="Platform liability (available)"
                  value={usd(data.totalUserBalances)}
                  color={COLORS.ORANGE}
                  iconName="people-outline"
                />
                <KpiCard
                  label="Gift / Promo Balances"
                  sublabel="Non-withdrawable wallet credits"
                  value={usd(data.totalGiftBalances || 0)}
                  color={COLORS.BLUE}
                  iconName="gift-outline"
                />
              </View>
              <View style={styles.kpiRow}>
                <KpiCard
                  label="Pools In-Play"
                  sublabel="Prize pools in live events"
                  value={usd(data.poolsInPlay)}
                  color={COLORS.SUCCESS}
                  iconName="trophy-outline"
                />
                <KpiCard
                  label="Pending Withdrawals"
                  sublabel={data.withdrawals.pending + ' request' + (data.withdrawals.pending !== 1 ? 's' : '') + ' queued'}
                  value={usd(data.withdrawals.pendingCents)}
                  color={data.withdrawals.pending > 0 ? COLORS.DANGER : COLORS.DIM}
                  iconName="arrow-down-circle-outline"
                />
              </View>

              {/* Net position summary bar */}
              <View style={styles.summaryBar}>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Total Wallet Value (all users)</Text>
                  <Text style={styles.summaryValue}>{usd(data.totalWalletValue)}</Text>
                </View>
                <View style={styles.divider} />
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Available Balances</Text>
                  <Text style={styles.summaryValueDim}>{usd(data.totalUserBalances)}</Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>+ Gift / Promo Balances</Text>
                  <Text style={styles.summaryValueDim}>{usd(data.totalGiftBalances || 0)}</Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>- Pending Withdrawals</Text>
                  <Text style={[styles.summaryValueDim, data.withdrawals.pendingCents > 0 && { color: COLORS.DANGER }]}>
                    {usd(data.withdrawals.pendingCents)}
                  </Text>
                </View>
              </View>
            </Section>

            {/* ============================================================ */}
            {/* SECTION 3: MONEY FLOW */}
            {/* ============================================================ */}
            <Section title="Money Flow (Ledger)" icon="swap-horizontal-outline">
              <View style={styles.kpiRow}>
                <KpiCard
                  label="Total Deposits"
                  sublabel="Stripe + gift cards"
                  value={usd(data.ledger.deposits)}
                  color={COLORS.SUCCESS}
                  iconName="arrow-up-circle-outline"
                />
                <KpiCard
                  label="Total Withdrawals"
                  sublabel="Turbo Pay + standard"
                  value={usd(data.ledger.withdrawals)}
                  color={COLORS.DANGER}
                  iconName="arrow-down-circle-outline"
                />
              </View>
              <View style={styles.kpiRow}>
                <KpiCard
                  label="Net Inflow"
                  sublabel="Deposits - withdrawals"
                  value={usd(data.ledger.deposits - data.ledger.withdrawals)}
                  color={data.ledger.deposits >= data.ledger.withdrawals ? COLORS.SUCCESS : COLORS.DANGER}
                  iconName="git-compare-outline"
                />
                <KpiCard
                  label="Ledger Transactions"
                  sublabel="Total entries recorded"
                  value={num(data.ledger.txCount)}
                  color={COLORS.DIM}
                  iconName="list-outline"
                />
              </View>

              {/* Flow breakdown bar */}
              <View style={styles.summaryBar}>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Entry Fees Collected</Text>
                  <Text style={styles.summaryValue}>{usd(data.ledger.entryFees)}</Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Pool Distributions</Text>
                  <Text style={styles.summaryValueDim}>{usd(data.ledger.distributions)}</Text>
                </View>
                {data.ledger.giftCards > 0 && (
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>Gift Card Redemptions</Text>
                    <Text style={styles.summaryValueDim}>{usd(data.ledger.giftCards)}</Text>
                  </View>
                )}
                {data.withdrawals.completed > 0 && (
                  <>
                    <View style={styles.divider} />
                    <View style={styles.summaryRow}>
                      <Text style={styles.summaryLabel}>Completed Withdrawals</Text>
                      <Text style={styles.summaryValueDim}>{num(data.withdrawals.completed)} totaling {usd(data.withdrawals.completedCents)}</Text>
                    </View>
                  </>
                )}
              </View>
            </Section>

            {/* ============================================================ */}
            {/* SECTION 4: EVENT ECONOMICS */}
            {/* ============================================================ */}
            <Section title="Event Economics" icon="podium-outline">
              <View style={styles.kpiRow}>
                <KpiCard
                  label="Total Events"
                  sublabel={num(data.eventTypeBreakdown.tournament) + ' tournaments, ' + num(data.eventTypeBreakdown.match) + ' H2H'}
                  value={num(data.totalEvents)}
                  color={COLORS.BLUE}
                  iconName="grid-outline"
                />
                <KpiCard
                  label="Total Entrants"
                  sublabel="Across all events"
                  value={num(data.totalEntrants)}
                  color={COLORS.TEAL}
                  iconName="people-outline"
                />
              </View>
              <View style={styles.kpiRow}>
                <KpiCard
                  label="Avg Entry Fee"
                  sublabel="Per participant"
                  value={usd(data.avgEntryFee)}
                  color={COLORS.GOLD}
                  iconName="ticket-outline"
                />
                <KpiCard
                  label="Avg Revenue/Event"
                  sublabel="Platform fee per event"
                  value={usd(data.avgRevenuePerEvent)}
                  color={COLORS.SUCCESS}
                  iconName="stats-chart-outline"
                />
              </View>

              {/* Event Status Breakdown */}
              <View style={styles.summaryBar}>
                <Text style={[styles.summaryLabel, { fontWeight: '700', color: COLORS.TEXT, marginBottom: 8 }]}>Events by Status</Text>
                {Object.entries(data.statusCounts)
                  .sort((a, b) => {
                    const order = ['live', 'created', 'locked', 'seeded', 'completed', 'finalized', 'cancelled'];
                    return order.indexOf(a[0]) - order.indexOf(b[0]);
                  })
                  .map(([status, count]) => (
                    <View key={status} style={styles.statusRow}>
                      <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: STATUS_COLORS[status] || COLORS.DIM, marginRight: 8 }} />
                      <Text style={styles.statusLabel}>{status.charAt(0).toUpperCase() + status.slice(1)}</Text>
                      <Text style={styles.statusCount}>{count}</Text>
                      <View style={styles.statusBarOuter}>
                        <View style={{ height: 6, backgroundColor: STATUS_COLORS[status] || COLORS.DIM, borderRadius: 3, width: (count / data.totalEvents * 100) + '%' }} />
                      </View>
                    </View>
                  ))}
              </View>
            </Section>

            {/* ============================================================ */}
            {/* SECTION 5: REVENUE BY GAME */}
            {/* ============================================================ */}
            <Section title="Revenue by Game" icon="game-controller-outline">
              {data.gameRanking.map((g, idx) => (
                <View key={g.game} style={styles.gameRow}>
                  <View style={styles.gameRank}>
                    <Text style={styles.gameRankText}>{idx + 1}</Text>
                  </View>
                  <View style={styles.gameInfo}>
                    <Text style={styles.gameName}>{g.game}</Text>
                    <Text style={styles.gameStats}>
                      {num(g.events)} events  |  {num(g.entrants)} entrants
                    </Text>
                  </View>
                  <View style={styles.gameFinancials}>
                    <Text style={styles.gameGmv}>{usd(g.gmvCents)}</Text>
                    <Text style={styles.gameFee}>{usd(g.feeCents)} fee</Text>
                  </View>
                </View>
              ))}
            </Section>

            {/* ============================================================ */}
            {/* SECTION 6: ADVERTISER REVENUE */}
            {/* ============================================================ */}
            <Section title="Advertiser Revenue" icon="megaphone-outline">
              <View style={styles.kpiRow}>
                <KpiCard
                  label="Ad Revenue (Spent)"
                  sublabel="Total collected from advertisers"
                  value={usd(data.adData.totalSpentCents)}
                  color={COLORS.SUCCESS}
                  iconName="cash-outline"
                />
                <KpiCard
                  label="Total Ad Budget"
                  sublabel="Committed across campaigns"
                  value={usd(data.adData.totalBudgetCents)}
                  color={COLORS.GOLD}
                  iconName="pie-chart-outline"
                />
              </View>
              <View style={styles.kpiRow}>
                <KpiCard
                  label="Impressions"
                  sublabel="Total ad views served"
                  value={num(data.adData.totalImpressions)}
                  color={COLORS.BLUE}
                  iconName="eye-outline"
                />
                <KpiCard
                  label="Clicks"
                  sublabel={'CTR: ' + (data.adData.totalImpressions > 0 ? pct(data.adData.totalClicks / data.adData.totalImpressions * 100) : '0.0%')}
                  value={num(data.adData.totalClicks)}
                  color={COLORS.TEAL}
                  iconName="finger-print-outline"
                />
              </View>
              <View style={styles.kpiRow}>
                <KpiCard
                  label="Active Campaigns"
                  sublabel={'of ' + num(data.adData.totalCampaigns) + ' total'}
                  value={num(data.adData.activeCampaigns)}
                  color={COLORS.PURPLE}
                  iconName="radio-outline"
                />
                <KpiCard
                  label="Advertisers"
                  sublabel="Registered accounts"
                  value={num(data.adData.advertisers)}
                  color={COLORS.ORANGE}
                  iconName="briefcase-outline"
                />
              </View>

              {/* Daily ad revenue mini-chart (text-based sparkline) */}
              {data.adData.dailyStats.length > 0 && (
                <View style={styles.summaryBar}>
                  <Text style={[styles.summaryLabel, { fontWeight: '700', color: COLORS.TEXT, marginBottom: 8 }]}>
                    Daily Ad Revenue (Last {data.adData.dailyStats.length} Days)
                  </Text>
                  {data.adData.dailyStats.map((day, idx) => {
                    const maxSpent = Math.max(...data.adData.dailyStats.map(d => d.spentCents || d.revenueCents || 0), 1);
                    const spent = day.spentCents || day.revenueCents || 0;
                    const barWidth = (spent / maxSpent * 100);
                    return (
                      <View key={day.date || idx} style={styles.statusRow}>
                        <Text style={[styles.summaryLabel, { width: 70, fontSize: 11 }]}>
                          {day.date ? day.date.slice(5) : 'Day ' + (idx + 1)}
                        </Text>
                        <View style={[styles.statusBarOuter, { flex: 1, marginRight: 8 }]}>
                          <View style={{ height: 6, backgroundColor: COLORS.SUCCESS, borderRadius: 3, width: barWidth + '%' }} />
                        </View>
                        <Text style={[styles.summaryLabel, { width: 60, textAlign: 'right', fontSize: 11 }]}>
                          {usd(spent)}
                        </Text>
                      </View>
                    );
                  })}
                  <View style={styles.divider} />
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>Avg eCPM</Text>
                    <Text style={styles.summaryValue}>
                      {data.adData.totalImpressions > 0
                        ? usd(Math.round(data.adData.totalSpentCents / data.adData.totalImpressions * 1000))
                        : '$0.00'}
                    </Text>
                  </View>
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>Budget Utilization</Text>
                    <Text style={styles.summaryValue}>
                      {data.adData.totalBudgetCents > 0
                        ? pct(data.adData.totalSpentCents / data.adData.totalBudgetCents * 100)
                        : '0.0%'}
                    </Text>
                  </View>
                </View>
              )}
            </Section>

            {/* ============================================================ */}
            {/* SECTION 6.5: OPERATING EXPENSES                                */}
            {/* Vendor invoices (GCP, Mux, AI APIs, etc.) — Phase 1+2 of       */}
            {/* accurate-accounting initiative. Manual entry via               */}
            {/* AdminExpensesScreen; Stripe processor fees auto-computed.      */}
            {/* ============================================================ */}
            <Section title="Operating Expenses" icon="receipt-outline">
              {!data.expenses ? (
                <View style={styles.summaryBar}>
                  <ActivityIndicator color={COLORS.GOLD} size="small" />
                  <Text style={[styles.summaryLabel, { textAlign: 'center', marginTop: 8 }]}>
                    Loading expense aggregations…
                  </Text>
                </View>
              ) : (
                <>
                  {/* KPI row 1: burn metrics (accrual basis — bucketed by billing-period end) */}
                  <View style={styles.kpiRow}>
                    <KpiCard
                      label="Burn This Month"
                      sublabel="By billing-period end"
                      value={usd(data.expenses.burnThisMonthCents)}
                      color={COLORS.GOLD}
                      iconName="trending-down-outline"
                    />
                    <KpiCard
                      label="Burn Last 30 Days"
                      sublabel="Rolling window"
                      value={usd(data.expenses.burnLast30dCents)}
                      color={COLORS.ORANGE}
                      iconName="time-outline"
                    />
                  </View>
                  <View style={styles.kpiRow}>
                    <KpiCard
                      label="Burn YTD"
                      sublabel="Year-to-date"
                      value={usd(data.expenses.burnYtdCents)}
                      color={COLORS.PURPLE}
                      iconName="calendar-outline"
                    />
                    <KpiCard
                      label="Avg Monthly Burn"
                      sublabel="Trailing 6mo with data"
                      value={usd(data.expenses.avgMonthlyBurnCents)}
                      color={COLORS.BLUE}
                      iconName="stats-chart-outline"
                    />
                  </View>

                  {/* KPI row 2: Stripe + unpaid */}
                  <View style={styles.kpiRow}>
                    <KpiCard
                      label="Stripe Fees (auto)"
                      sublabel={data.expenses.atlasWaiverActive
                        ? `Waiver active — ${data.expenses.stripeFeeDepositCount} deposits`
                        : `Across ${data.expenses.stripeFeeDepositCount} deposits`}
                      value={usd(data.expenses.stripeFeesAutoCents)}
                      color={COLORS.TEAL}
                      iconName="card-outline"
                    />
                    <KpiCard
                      label="Unpaid Invoices"
                      sublabel={`${data.expenses.expenseCount || 0} entries in window`}
                      value={usd(data.expenses.unpaidCents)}
                      color={(data.expenses.unpaidCents || 0) > 0 ? COLORS.DANGER : COLORS.SUCCESS}
                      iconName="alert-circle-outline"
                    />
                  </View>

                  {/* Atlas waiver banner — info if active, warning if expired */}
                  <View style={data.expenses.atlasWaiverActive ? styles.expenseInfoBanner : styles.expenseWarnBanner}>
                    <Ionicons
                      name={data.expenses.atlasWaiverActive ? 'shield-checkmark' : 'alert-circle'}
                      size={14}
                      color={data.expenses.atlasWaiverActive ? COLORS.SUCCESS : COLORS.DANGER}
                    />
                    <Text style={[styles.summaryLabel, { flex: 1, fontSize: 11, lineHeight: 16, marginLeft: 8 }]}>
                      {data.expenses.atlasWaiverActive
                        ? `Stripe Atlas processing-fee waiver active through ${data.expenses.atlasWaiverExpiresAt}. Auto-tracked Stripe fees will start growing after that date.`
                        : `Stripe Atlas processing-fee waiver expired ${data.expenses.atlasWaiverExpiresAt} — fees are now accruing automatically into the auto-tracked line above.`}
                    </Text>
                  </View>

                  {/* Burn by Category breakdown */}
                  {Object.keys(data.expenses.byCategory || {}).length > 0 && (
                    <View style={styles.summaryBar}>
                      <Text style={[styles.summaryLabel, { fontWeight: '700', color: COLORS.TEXT, marginBottom: 8 }]}>
                        Burn by Category (Trailing 12 Months)
                      </Text>
                      {Object.entries(data.expenses.byCategory)
                        .sort((a, b) => (b[1].totalCents || 0) - (a[1].totalCents || 0))
                        .map(([catId, agg]) => {
                          const total = data.expenses.totalCents || 1;
                          const pctVal = ((agg.totalCents || 0) / total) * 100;
                          const color = agg.color || EXPENSE_CATEGORIES[catId]?.color || COLORS.DIM;
                          return (
                            <View key={catId} style={styles.statusRow}>
                              <Text style={[styles.summaryLabel, { width: 130, fontSize: 11 }]}>
                                {agg.displayName || EXPENSE_CATEGORIES[catId]?.displayName || catId}
                              </Text>
                              <View style={[styles.statusBarOuter, { flex: 1, marginRight: 8 }]}>
                                <View style={{ height: 6, backgroundColor: color, borderRadius: 3, width: pctVal + '%' }} />
                              </View>
                              <Text style={[styles.summaryLabel, { width: 90, textAlign: 'right', fontSize: 11 }]}>
                                {usd(agg.totalCents)}
                              </Text>
                              <Text style={[styles.summaryLabel, { width: 50, textAlign: 'right', fontSize: 11, color: COLORS.DIM }]}>
                                {pct(pctVal)}
                              </Text>
                            </View>
                          );
                        })}
                      <View style={styles.divider} />
                      <View style={styles.summaryRow}>
                        <Text style={styles.summaryLabel}>Total (12mo)</Text>
                        <Text style={styles.summaryValue}>{usd(data.expenses.totalCents)}</Text>
                      </View>
                    </View>
                  )}

                  {/* Top Vendors */}
                  {Object.keys(data.expenses.byVendor || {}).length > 0 && (
                    <View style={styles.summaryBar}>
                      <Text style={[styles.summaryLabel, { fontWeight: '700', color: COLORS.TEXT, marginBottom: 8 }]}>
                        Top Vendors (Trailing 12 Months)
                      </Text>
                      {Object.entries(data.expenses.byVendor)
                        .sort((a, b) => (b[1].totalCents || 0) - (a[1].totalCents || 0))
                        .slice(0, 8)
                        .map(([vendorId, agg]) => {
                          const total = data.expenses.totalCents || 1;
                          const pctVal = ((agg.totalCents || 0) / total) * 100;
                          return (
                            <View key={vendorId} style={styles.statusRow}>
                              <Text style={[styles.summaryLabel, { width: 130, fontSize: 11 }]} numberOfLines={1}>
                                {agg.shortName || agg.displayName || vendorId}
                              </Text>
                              <View style={[styles.statusBarOuter, { flex: 1, marginRight: 8 }]}>
                                <View style={{ height: 6, backgroundColor: COLORS.GOLD, borderRadius: 3, width: pctVal + '%' }} />
                              </View>
                              <Text style={[styles.summaryLabel, { width: 90, textAlign: 'right', fontSize: 11 }]}>
                                {usd(agg.totalCents)}
                              </Text>
                              <Text style={[styles.summaryLabel, { width: 50, textAlign: 'right', fontSize: 11, color: COLORS.DIM }]}>
                                {agg.count} inv.
                              </Text>
                            </View>
                          );
                        })}
                    </View>
                  )}
                </>
              )}
            </Section>

            {/* ============================================================ */}
            {/* SECTION 7: USER ECONOMICS */}
            {/* ============================================================ */}
            <Section title="User Economics" icon="person-outline">
              <View style={styles.kpiRow}>
                <KpiCard
                  label="Total Users"
                  sublabel="Registered accounts"
                  value={num(data.totalUsers)}
                  color={COLORS.BLUE}
                  iconName="people-outline"
                />
                <KpiCard
                  label="Phone Verified"
                  sublabel={data.totalUsers > 0 ? pct(data.verifiedUsers / data.totalUsers * 100) + ' of users' : 'N/A'}
                  value={num(data.verifiedUsers)}
                  color={COLORS.SUCCESS}
                  iconName="call-outline"
                />
              </View>
              <View style={styles.kpiRow}>
                <KpiCard
                  label="ID Verified (KYC)"
                  sublabel={data.totalUsers > 0 ? pct(data.idVerifiedUsers / data.totalUsers * 100) + ' of users' : 'N/A'}
                  value={num(data.idVerifiedUsers)}
                  color={COLORS.TEAL}
                  iconName="shield-checkmark-outline"
                />
                <KpiCard
                  label="Avg Wallet Balance"
                  sublabel="Per user"
                  value={usd(data.avgBalanceCents)}
                  color={COLORS.ORANGE}
                  iconName="wallet-outline"
                />
              </View>
            </Section>

            {/* Disclaimer */}
            <View style={styles.disclaimer}>
              <Ionicons name="information-circle-outline" size={14} color={COLORS.DIM} />
              <Text style={styles.disclaimerText}>
                All figures computed from Firestore in real-time. GMV = total entry fees. Revenue = platform fees (10%).
                CritCash is a custodian of player funds, not a prize sponsor.
              </Text>
            </View>
          </>
        )}
      </ScrollView>
      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

// ============================================================================
// UI COMPONENTS
// ============================================================================

const Section = ({ title, icon, children }) => (
  <View style={styles.section}>
    <View style={styles.sectionHeader}>
      <Ionicons name={icon} size={18} color={COLORS.GOLD} />
      <Text style={styles.sectionTitle}>{title}</Text>
    </View>
    {children}
  </View>
);

const KpiCard = ({ label, sublabel, value, color, iconName }) => (
  <View style={styles.kpiBox}>
    <View style={styles.kpiHeader}>
      <Ionicons name={iconName} size={16} color={color || COLORS.DIM} />
      <Text style={styles.kpiLabel}>{label}</Text>
    </View>
    <Text style={[styles.kpiValue, color && { color }]}>{value}</Text>
    {sublabel && <Text style={styles.kpiSublabel}>{sublabel}</Text>}
  </View>
);

// ============================================================================
// STYLES
// ============================================================================

const STATUS_COLORS = {
  created: '#3498DB',
  locked: '#F39C12',
  seeded: '#F39C12',
  live: '#2ECC71',
  completed: '#9B59B6',
  finalized: '#1ABC9C',
  cancelled: '#E74C3C',
};

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: COLORS.BG },
  centerWrap: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 },
  deniedText: { color: COLORS.DANGER, fontSize: 18, fontWeight: 'bold', textAlign: 'center' },
  backLink: { color: COLORS.DIM, marginTop: 16, textDecorationLine: 'underline' },

  container: { padding: 16, paddingBottom: 120 },
  header: { alignItems: 'center', marginBottom: 12 },
  title: { fontSize: 28, fontWeight: '900', color: COLORS.GOLD, textAlign: 'center' },
  subtitle: { color: COLORS.DIM, fontSize: 14, marginTop: 4, textAlign: 'center' },

  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    alignSelf: 'flex-start',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 6,
    backgroundColor: COLORS.CARD,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
  },
  backButtonText: { color: COLORS.GOLD, fontWeight: '700', fontSize: 14 },

  compCard: {
    backgroundColor: COLORS.CARD, borderWidth: 1, borderColor: 'rgba(252,208,0,0.35)',
    borderRadius: 12, padding: 16, marginBottom: 16,
  },
  compHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  compTitle: { color: COLORS.TEXT, fontSize: 16, fontWeight: '800' },
  compBadge: { borderRadius: 6, paddingHorizontal: 10, paddingVertical: 3, borderWidth: 1 },
  compBadgeTest: { backgroundColor: 'rgba(252,208,0,0.1)', borderColor: 'rgba(252,208,0,0.4)' },
  compBadgeLive: { backgroundColor: 'rgba(231,76,60,0.12)', borderColor: 'rgba(231,76,60,0.45)' },
  compBadgeTxt: { color: COLORS.TEXT, fontSize: 11, fontWeight: '800' },
  compRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 4 },
  compLabel: { color: COLORS.DIM, fontSize: 13, flex: 1 },
  compVal: { color: COLORS.TEXT, fontSize: 14, fontWeight: '700' },
  compNetRow: { borderTopWidth: 1, borderTopColor: COLORS.BORDER, marginTop: 4, paddingTop: 8 },
  compNetLabel: { color: COLORS.TEXT, fontSize: 13, fontWeight: '800', flex: 1, marginRight: 10 },
  compNet: { fontSize: 16, fontWeight: '800' },
  compNote: { color: COLORS.DIM, fontSize: 11, marginTop: 8, lineHeight: 16 },
  compBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    backgroundColor: COLORS.GOLD, borderRadius: 8, paddingVertical: 10, marginTop: 12,
  },
  compBtnTxt: { color: '#000', fontSize: 13, fontWeight: '800' },

  loadingWrap: { alignItems: 'center', paddingVertical: 60 },
  dimText: { color: COLORS.DIM, fontSize: 14 },
  retryBtn: { marginTop: 16, paddingVertical: 10, paddingHorizontal: 24, borderRadius: 6, backgroundColor: COLORS.GOLD },
  retryBtnText: { color: '#000', fontWeight: '700' },

  // Section
  section: { width: '100%', marginBottom: 28 },
  sectionHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 14 },
  sectionTitle: { color: COLORS.TEXT, fontSize: 17, fontWeight: '800', marginLeft: 8 },

  // KPI Cards
  kpiRow: { flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -5, marginBottom: 4 },
  kpiBox: {
    flex: 1,
    minWidth: '45%',
    margin: 5,
    backgroundColor: COLORS.CARD,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    padding: 14,
  },
  kpiHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  kpiLabel: { color: COLORS.DIM, fontSize: 11, fontWeight: '600', marginLeft: 6, flex: 1 },
  kpiValue: { color: COLORS.TEXT, fontSize: 22, fontWeight: '800' },
  kpiSublabel: { color: COLORS.DIM, fontSize: 10, marginTop: 4 },

  // Summary bars
  summaryBar: {
    backgroundColor: COLORS.CARD,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    padding: 14,
    marginTop: 8,
  },
  summaryRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 5 },
  summaryLabel: { color: COLORS.DIM, fontSize: 13 },
  summaryValue: { color: COLORS.TEXT, fontSize: 15, fontWeight: '700' },
  summaryValueDim: { color: COLORS.DIM, fontSize: 13, fontWeight: '600' },
  divider: { height: 1, backgroundColor: COLORS.BORDER, marginVertical: 8 },

  // Operating-expenses Atlas-waiver banner (matches summaryBar visual weight)
  expenseInfoBanner: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: 'rgba(46, 204, 113, 0.06)',
    borderColor: COLORS.SUCCESS, borderWidth: 1, borderRadius: 8,
    padding: 12, marginTop: 8,
  },
  expenseWarnBanner: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: 'rgba(231, 76, 60, 0.08)',
    borderColor: COLORS.DANGER, borderWidth: 1, borderRadius: 8,
    padding: 12, marginTop: 8,
  },

  // Status breakdown
  statusRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 4 },
  statusLabel: { color: COLORS.TEXT, fontSize: 13, width: 80 },
  statusCount: { color: COLORS.DIM, fontSize: 13, fontWeight: '700', width: 30, textAlign: 'right', marginRight: 10 },
  statusBarOuter: { flex: 1, height: 6, backgroundColor: '#1a1a1a', borderRadius: 3, overflow: 'hidden' },

  // Game breakdown
  gameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.CARD,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    padding: 12,
    marginBottom: 8,
  },
  gameRank: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#1a1a1a',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  gameRankText: { color: COLORS.GOLD, fontSize: 12, fontWeight: '800' },
  gameInfo: { flex: 1 },
  gameName: { color: COLORS.TEXT, fontSize: 14, fontWeight: '700' },
  gameStats: { color: COLORS.DIM, fontSize: 11, marginTop: 2 },
  gameFinancials: { alignItems: 'flex-end' },
  gameGmv: { color: COLORS.TEXT, fontSize: 15, fontWeight: '700' },
  gameFee: { color: COLORS.SUCCESS, fontSize: 11, marginTop: 2 },

  // Disclaimer
  disclaimer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 12,
    paddingHorizontal: 8,
    marginTop: 8,
  },
  disclaimerText: { color: COLORS.DIM, fontSize: 11, marginLeft: 6, flex: 1, lineHeight: 16 },
});